var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { apiClient, createApiError } from '../interceptor/interceptor';
// ==========================================
// DATE FORMATTING
// ==========================================
/**
 * Formats ISO date to "DD Mon YYYY H:MMam/pm".
 */
export const formatDateWithTime = (isoDate) => {
    try {
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) {
            return isoDate;
        }
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        const hour12 = hours % 12 || 12;
        return `${day} ${month} ${year} ${hour12}:${minutes}${ampm}`;
    }
    catch (_a) {
        return isoDate;
    }
};
// ==========================================
// RESPONSE PARSING
// ==========================================
/**
 * Derives a severity label from the raw risk score when the backend does not
 * provide one (e.g. the severity feature flag is off).
 *
 * Thresholds (per OZ-726):
 *   < 0.33        → MILD
 *   0.33 – 0.66   → MODERATE
 *   > 0.66        → SEVERE
 */
export const deriveSeverity = (score) => {
    if (score > 0.66)
        return 'SEVERE';
    if (score >= 0.33)
        return 'MODERATE';
    return 'MILD';
};
const parseOsaFactorsResponse = (data) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const rawData = data;
    // topContributingFactors are AI-derived (STOP-BANG + SHAP). Backend may also
    // return them alongside a soft error if previous or fallback predictions exist.
    const factors = ((_a = rawData.topContributingFactors) === null || _a === void 0 ? void 0 : _a.length)
        ? rawData.topContributingFactors.map((rf) => (Object.assign({ label: rf.label }, (rf.date ? { date: rf.date } : {}))))
        : [];
    const pastHistory = ((_b = rawData.pastHistory) === null || _b === void 0 ? void 0 : _b.length)
        ? rawData.pastHistory.map((item) => (Object.assign({ category: item.category, label: item.label }, (item.date ? { date: item.date } : {}))))
        : [];
    const metadata = rawData.scoreComputedDate
        ? {
            score: (_c = rawData.score) !== null && _c !== void 0 ? _c : 0,
            severity: (_d = rawData.severity) !== null && _d !== void 0 ? _d : deriveSeverity((_e = rawData.score) !== null && _e !== void 0 ? _e : 0),
            date: formatDateWithTime(rawData.scoreComputedDate),
            scoreComputedDate: rawData.scoreComputedDate,
            predictionStaleThresholdDays: (_f = rawData.predictionStaleThresholdDays) !== null && _f !== void 0 ? _f : 30
        }
        : null;
    // Soft error: data was fetched from the EHR but ML scoring failed. The backend
    // returns HTTP 200 with predictionError=true and surfaces the error/retry state.
    if (data.predictionError) {
        return {
            patientAlias: data.patientAlias,
            factors,
            pastHistory,
            metadata,
            hasActiveSleepOrder: (_g = data.hasActiveSleepOrder) !== null && _g !== void 0 ? _g : false,
            predictionError: true,
            errorMessage: data.errorMessage,
            retryAvailable: (_h = data.retryAvailable) !== null && _h !== void 0 ? _h : false
        };
    }
    return {
        patientAlias: data.patientAlias,
        factors,
        pastHistory,
        metadata,
        hasActiveSleepOrder: data.hasActiveSleepOrder
    };
};
// ==========================================
// API FUNCTIONS
// ==========================================
/**
 * Fetches OSA risk factors for a patient.
 * The backend provides pre-selected, pre-formatted risk factors.
 *
 * @param patientId - Patient ID (required, non-empty string)
 * @returns Object containing risk factors and metadata
 * @throws ApiError if request fails (handled by interceptor)
 */
export const getOsaFactors = (patientId) => __awaiter(void 0, void 0, void 0, function* () {
    if (!(patientId === null || patientId === void 0 ? void 0 : patientId.trim())) {
        throw createApiError('Patient ID is required', 400);
    }
    const response = yield apiClient.get(`screener/patients/${patientId}/osa-factors`);
    return parseOsaFactorsResponse(response.data);
});
/**
 * Refreshes (recalculates) OSA prediction for a patient.
 *
 * @param patientId - Patient ID (required, non-empty string)
 * @returns Object containing updated risk factors and metadata
 * @throws ApiError if request fails (handled by interceptor)
 */
export const refreshOsaPrediction = (patientId) => __awaiter(void 0, void 0, void 0, function* () {
    if (!(patientId === null || patientId === void 0 ? void 0 : patientId.trim())) {
        throw createApiError('Patient ID is required', 400);
    }
    const response = yield apiClient.get(`screener/patients/${patientId}/osa-factors?refresh=true`);
    return parseOsaFactorsResponse(response.data);
});
